import type {
  TransferReviewDetails,
  TransferReviewDetailsError,
} from '@varo/graphql-schema-types';
import {
  ANALYTICS_SOURCES,
  apiErrorFailure,
  pressableAttributes,
  type PressableComponentType,
  requestErrorFailure,
  type ReviewedTransfer,
  reviewedTransferAttributes,
  SUBMIT_OUTCOME_UNKNOWN,
  type TransferFailure,
} from 'features/feeBasedInstantTransfer/helpers/analyticsFormatters';
import useFailureTransition from 'features/feeBasedInstantTransfer/hooks/useFailureTransition';
import useOperationAnalytics from 'features/feeBasedInstantTransfer/hooks/useOperationAnalytics';
import useTransferAnalytics from 'features/feeBasedInstantTransfer/hooks/useTransferAnalytics';
import { TRANSFER_MONEY_REQUEST_SUBMITTED } from 'shared/analytics/events/transferMoney';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import type { UseQueryState } from 'urql';

const CONFIRM_SCREEN_KEY = 'instantTransferConfirm';

const LEAVE_DIALOG_SCREEN_KEY = 'instantTransferConfirmLeaveDialog';

const REVIEW_PATH = '/move-money/transfer/review-transfer-details';

const LEAVE_DIALOG_SCREEN_ATTRIBUTES = {
  screen_name: 'Transfer Money Confirm Leave Dialog',
  deeplink: REVIEW_PATH,
};

const LEAVE_DIALOG_BUTTONS = {
  stay: {
    key: `${REVIEW_PATH}/transferMoneyConfirmLeaveDialogStay`,
    pressableName: 'Stay',
  },
  leave: {
    key: `${REVIEW_PATH}/transferMoneyConfirmLeaveDialogLeave`,
    pressableName: 'Leave',
  },
} as const;

const ERROR_BUTTONS = {
  retry: {
    analyticsId: 'transferMoneyConfirmErrorRetry',
    pressableName: 'Retry',
  },
  done: { analyticsId: 'transferMoneyConfirmErrorDone', pressableName: 'Done' },
} as const;

const INVALID_REVIEW_INPUT_FAILURE: TransferFailure = {
  reason: 'invalid_review_input',
  type: 'client',
};

const EMPTY_REVIEW_FAILURE: TransferFailure = {
  reason: 'review_empty',
  type: 'server',
};

const MISSING_REVIEW_FAILURE: TransferFailure = {
  reason: 'review_missing',
  type: 'server',
};

interface TransferConfirmAnalyticsState {
  input: ReviewedTransfer | null;
  reviewResult: UseQueryState;
  review: TransferReviewDetails | TransferReviewDetailsError | null | undefined;
  details: TransferReviewDetails | undefined;
  reviewError: TransferReviewDetailsError | undefined;
  isOutcomeUnknown: boolean;
  isConfirmingLeave: boolean;
}

export default function useTransferConfirmAnalytics({
  input,
  reviewResult,
  review,
  details,
  reviewError,
  isOutcomeUnknown,
  isConfirmingLeave,
}: TransferConfirmAnalyticsState) {
  const { logTransferEvent, logFlowError } = useTransferAnalytics(
    ANALYTICS_SOURCES.confirm,
  );
  const { analyticsEvents } = useAnalyticsConfiguration();
  const transferAttributes = input
    ? reviewedTransferAttributes(input, details?.rail, details?.transferDetails)
    : {};

  const getReviewFailure = (): TransferFailure | undefined => {
    if (!input) {
      return INVALID_REVIEW_INPUT_FAILURE;
    }
    if (reviewResult.fetching || details) {
      return undefined;
    }
    if (reviewResult.error) {
      return requestErrorFailure(reviewResult.error, 'review_request_failed');
    }
    if (reviewError) {
      return apiErrorFailure(reviewError.errorCode, reviewError.message);
    }
    return review ? EMPTY_REVIEW_FAILURE : MISSING_REVIEW_FAILURE;
  };
  const reviewFailure = getReviewFailure();

  useOperationAnalytics(
    ANALYTICS_SOURCES.confirm,
    'transfer_review_details',
    reviewResult,
    review,
    !input,
  );
  useFailureTransition(reviewFailure, (failure) =>
    logFlowError('confirm', failure, transferAttributes),
  );
  // Logged on landing, before the review loads, so every arrival is counted whatever mode follows.
  useViewDidFocusScreenAnalyticsEvent({ key: CONFIRM_SCREEN_KEY });
  useViewDidFocusScreenAnalyticsEvent(
    isConfirmingLeave
      ? {
          key: LEAVE_DIALOG_SCREEN_KEY,
          customAttributes: LEAVE_DIALOG_SCREEN_ATTRIBUTES,
        }
      : null,
  );

  const getShownErrorAttributes = () => {
    if (isOutcomeUnknown) {
      return { step_name: 'submit', error_reason: SUBMIT_OUTCOME_UNKNOWN };
    }
    return reviewFailure
      ? { step_name: 'confirm', error_reason: reviewFailure.reason }
      : null;
  };

  const errorButtonAnalytics = (
    button: keyof typeof ERROR_BUTTONS,
    componentType: PressableComponentType,
  ) => ({
    analyticsId: ERROR_BUTTONS[button].analyticsId,
    customAttributes: pressableAttributes(
      ERROR_BUTTONS[button].pressableName,
      componentType,
      getShownErrorAttributes(),
    ),
  });

  const logRequestSubmitted = () => {
    logTransferEvent(TRANSFER_MONEY_REQUEST_SUBMITTED, transferAttributes);
  };

  const logLeaveDialogChoice = (choice: keyof typeof LEAVE_DIALOG_BUTTONS) => {
    const { key, pressableName } = LEAVE_DIALOG_BUTTONS[choice];
    void analyticsEvents.logPressableEventFromConfig(
      key,
      pressableAttributes(pressableName, 'dialog_action'),
    );
  };

  return { errorButtonAnalytics, logRequestSubmitted, logLeaveDialogChoice };
}
